/**
 * MiniImageCarousel
 * Lightweight carousel for product card thumbnails (first 3 images).
 * Features loop navigation, arrow controls without circular backgrounds,
 * and dot pagination (white dots with green active indicator).
 */
export class MiniImageCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.slides = Array.from(this.querySelectorAll("[data-carousel-slide]"));
    this.dots = Array.from(this.querySelectorAll("[data-carousel-dot]"));
    this.prevBtn = this.querySelector("[data-carousel-prev]");
    this.nextBtn = this.querySelector("[data-carousel-next]");
    this.currentIndex = 0;

    if (!this.track || this.slides.length <= 1) return;

    this.bindEvents();
  }

  isMobile() {
    return window.matchMedia("(max-width: 767.98px)").matches;
  }

  bindEvents() {
    this.prevBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.goTo(this.currentIndex - 1);
    });

    this.nextBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.goTo(this.currentIndex + 1);
    });

    // Touch swipe support (desktop / tablet only, disabled on mobile)
    let startX = 0;
    let startY = 0;
    let isSwiping = false;

    this.addEventListener(
      "touchstart",
      (e) => {
        if (this.isMobile()) return;
        if (e.touches.length === 1) {
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          isSwiping = true;
        }
      },
      { passive: true },
    );

    this.addEventListener(
      "touchmove",
      (e) => {
        if (!isSwiping || e.touches.length !== 1 || this.isMobile()) return;
        const diffX = e.touches[0].clientX - startX;
        const diffY = e.touches[0].clientY - startY;
        if (Math.abs(diffX) > Math.abs(diffY)) {
          if (e.cancelable) e.preventDefault();
        }
      },
      { passive: false },
    );

    this.addEventListener(
      "touchend",
      (e) => {
        if (!isSwiping || this.isMobile()) return;
        isSwiping = false;
        const endX = e.changedTouches[0].clientX;
        const diffX = endX - startX;
        if (Math.abs(diffX) > 20) {
          if (diffX > 0) {
            this.goTo(this.currentIndex - 1);
          } else {
            this.goTo(this.currentIndex + 1);
          }
        }
      },
      { passive: true },
    );

    window.matchMedia("(max-width: 767.98px)").addEventListener("change", (e) => {
      if (e.matches) {
        this.currentIndex = 0;
        if (this.track) this.track.style.transform = "";
      }
    });
  }

  goTo(index) {
    if (this.isMobile()) return;
    const count = this.slides.length;
    if (count <= 1) return;

    // Wrap around
    this.currentIndex = (index + count) % count;

    this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;

    this.dots.forEach((dot, i) => {
      dot.classList.toggle("is-active", i === this.currentIndex);
    });

    this.slides.forEach((slide, i) => {
      slide.setAttribute("tabindex", i === this.currentIndex ? "0" : "-1");
    });
  }
}

if (!customElements.get("mini-image-carousel")) {
  customElements.define("mini-image-carousel", MiniImageCarousel);
}
